import { X } from "lucide-react";

import { Progress } from "@/components/ui/progress";

import { ReactComponent as CompleteIcon } from "@assets/svg/check-circled-filled.svg";
import { ReactComponent as DocumentIcon } from "@assets/svg/document-filled.svg";
import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";

import { stripBundlePrefix } from "@utils/bundle-name";
import { formatFileSize } from "@utils/fileFormat";

// MDRS-550: a name longer than this can truncate in the bundle pop-up, so the
// ellipsis goes in the middle — long bundle names tend to differ only near the
// end ("…Records Part 2.pdf") — and its last characters stay visible.
const SPLIT_FROM_LENGTH = 40;
const TAIL_LENGTH = 16;

// By code point, so a split never lands inside a surrogate pair.
const splitName = (name: string): [string, string] => {
  const chars = Array.from(name);
  if (chars.length <= SPLIT_FROM_LENGTH) return [name, ""];
  return [chars.slice(0, -TAIL_LENGTH).join(""), chars.slice(-TAIL_LENGTH).join("")];
};

// Define prop types for the component
type FilePreviewProps = {
  file: File;
  isSuccess?: boolean;
  isFailed?: boolean;
  isLoading?: boolean;
  uploadProgress?: number;
  onClear?: () => void;
};

// FilePreview component definition
const FilePreview = ({
  file,
  isSuccess: _isSuccess = false,
  isFailed = false,
  isLoading = false,
  uploadProgress,
  onClear,
}: FilePreviewProps) => {
  const displayName = stripBundlePrefix(file.name);
  const [head, tail] = splitName(displayName);

  return (
    <div
      className={`w-full rounded-xl ${isFailed ? "bg-destructive-50" : "bg-background-white"} px-2 py-3`}
    >
      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2">
        <figure
          className={`rounded-full ${isFailed ? "bg-destructive-100" : "bg-secondary-50"} p-2`}
        >
          <DocumentIcon
            className={`size-5 ${isFailed ? "[&_path]:fill-destructive" : "[&_path]:fill-secondary"}`}
          />
        </figure>

        <div className="flex min-w-0 items-center gap-2">
          {tail ? (
            // whitespace-pre: each half is its own flex item, which would
            // otherwise drop a space at the split.
            <span className="flex min-w-0 text-sm font-medium" title={displayName}>
              <span className="overflow-hidden text-ellipsis whitespace-pre">
                {head}
              </span>
              <span className="flex-none whitespace-pre">{tail}</span>
            </span>
          ) : (
            <span className="truncate text-sm font-medium" title={displayName}>
              {displayName}
            </span>
          )}{" "}
          <span className="shrink-0 text-xs text-foreground-muted">
            {formatFileSize(file.size)}
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          {isFailed ? (
            <DocumentIcon className="size-4 [&_path]:fill-destructive" />
          ) : isLoading || uploadProgress !== undefined ? (
            <LoadingIcon className="size-4 animate-spin-ccw" />
          ) : (
            <CompleteIcon className="size-4 [&_path]:fill-success" />
          )}
          {onClear && (
            <button
              onClick={onClear}
              className="text-foreground-muted hover:text-foreground"
              aria-label="Remove file"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      </div>

      {uploadProgress !== undefined && uploadProgress < 100 && (
        <div className="mt-2 px-1">
          <Progress
            value={uploadProgress > 0 ? uploadProgress : undefined}
            className="h-2"
          />
        </div>
      )}
    </div>
  );
};

// Export the component
export { FilePreview };
